import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { addMinutes, format } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import { ICalendarEvent } from '../../../../../../components/ui/calendar/calendars';
import { TExistingEVisitRule } from '../../../../../../types/e-visits';
import { PreviewFormSC } from './preview-form.styled';
import Button from '@hilbi/web-common/components/e-visits/button';
import { fitAllBlocksInInterval } from '../../../../utils';
import { ReactComponent as RepeatIcon } from '../../../../../../assets/svg/e_visit_repeat.svg';
import { isRepeatingRule } from '../add-form/add-form.util';
import MoreButton from './more-button';

interface PreviewFormProps {
  event: ICalendarEvent<TExistingEVisitRule>;
  onClose: () => void;
}

const PreviewForm: React.FunctionComponent<PreviewFormProps> = ({ event, onClose }) => {
  const start = new Date(event.start);
  const end = new Date(event.end);

  const [displayAll, setDisplayAll] = useState(false);

  const blockSize = event.eventData?.blockSize || 1;
  const { fullBlocks } = fitAllBlocksInInterval(start, end, blockSize);

  const ranges = [...new Array(Math.min(fullBlocks, displayAll ? fullBlocks : 6)).keys()].map(
    (k) => {
      const s = addMinutes(start, k * blockSize);
      const e = addMinutes(s, blockSize);
      return { s, e };
    },
  );
  const isRepeating = isRepeatingRule(event);

  const Description = useMemo(() => {
    const doctorDescription = event.eventData?.description;

    return doctorDescription ? (
      <>
        <div className="description-title">
          <FormattedMessage id="calendarMenu_doctor_description" />
        </div>
        <p className="description">{doctorDescription}</p>
      </>
    ) : null;
  }, [event]);

  return (
    <PreviewFormSC>
      <div className="overview">
        <FormattedMessage
          id="previewForm_title"
          values={{
            // eslint-disable-next-line react/display-name
            block: event.eventData?.blockSize || '0',
            time: `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`,
            // @ts-expect-error
            b: (ch) => <b>{ch}</b>,
          }}
        />
      </div>
      <div className="pill-wrapper">
        {ranges.map(({ s, e }) => (
          <div className="time-pill " key={`${s}-${e}`}>
            {format(s, 'HH:mm')} - {format(e, 'HH:mm')}
          </div>
        ))}
        <MoreButton
          displayAll={displayAll}
          setDisplayAll={setDisplayAll}
          displayedNum={fullBlocks - ranges.length}
        />
      </div>
      {isRepeating && (event.eventData?.fromDate || event.eventData?.toDate) && (
        <div className="repeat-container">
          <RepeatIcon />
          <FormattedMessage
            id="previewForm_repeat"
            values={{
              day: moment(event.eventData.fromDate).format('dddd'),
              dayNumber: event.eventData.fromDate
                ? new Date(event.eventData.fromDate).getDay()
                : '',
              date: event.eventData.toDate ? moment(event.eventData.toDate).format('ll') : '',
            }}
          />
        </div>
      )}
      {Description}
      <Button type="submit" variation={'primary'} onClick={onClose} isBlock>
        <FormattedMessage id="previewForm_cancel" />
      </Button>
    </PreviewFormSC>
  );
};

export default React.memo(PreviewForm);
